export class BillingInfoNotFoundError extends Error {
  readonly code = 404;
  readonly reason = "We don't have your billing information on file.";

  constructor(message = "Billing info not found") {
    super(message);
    Object.setPrototypeOf(this, BillingInfoNotFoundError.prototype);
  }
}

export interface ZypeTokenData {
  resource_owner_id: string;
  scopes: string[];
  expires_in_seconds: number;
  application: {
    uid: string;
  };
  created_at: number;
}

export interface ZypeTokenResponse extends PwsBaseResponse {
  tokenData: ZypeTokenData;
  refreshed: boolean;
  user: ZypeUser;
  subscriptions?: ZypeSubscription[];

  accessToken?: string;
  refreshToken?: string | null;
}

export interface ZypeLoginData {
  consumer_id: string;
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  scope: string;
  created_at: number;
}

export interface ZypeUser {
  _id: string;
  amazon_user_id: string | null;
  birthday: string | null;
  braintree_id: string | null;
  created_at: string;
  current_sign_in_at: string | null;
  current_sign_in_ip: string | null;
  days_trialed: number;
  email: string;
  has_trialed: boolean;
  last_sign_in_at: string | null;
  last_sign_in_ip: string | null;
  name: string | null;
  pass_count: number;
  password_token: string | null;
  playlist_count: number;
  remember_token: string | null;
  rss_token: string;
  sex: string | null;
  sign_in_count: number;
  site_id: string;
  stripe_id: string | null;
  subscription_count: number;
  terms: boolean;
  transaction_count: number;
  updated_at: string;
  updates: boolean;
  video_count: number;
  linked_devices: unknown[];
  subscription_plans: UserSubscriptionPlan[];
  subscription_ids: string[];
  password_hint?: null | string;
  recurly_code?: null | string;
  has_active_subscription: boolean;
  privacy_optout?: boolean;
  user_preferences?: Record<string, unknown>;
}

export interface UserSubscriptionPlan {
  id: string;
  name: string;
  entitlement_type: string;
  subscription_id: string;
}

export interface ZypeSubscriptionPlan {
  _id: string;
  _keywords: string[];
  active: boolean;
  amazon_id: string;
  amount: string;
  braintree_id: string | null;
  created_at: string;
  currency: string;
  deleted_at: string | null;
  description: string;
  discovery_url: string;
  entitlement_type: string;
  interval: string;
  interval_count: number;
  marketplace_ids: Record<string, unknown>;
  name: string;
  playlist_ids: string[];
  position: number;
  recurly_code: string;
  site_id: string;
  stripe_id: string | null;
  third_party_id: string;
  trial_period_days: number;
  updated_at: string;
}

export interface ZypeSubscription {
  _id: string;
  _keywords: string[];
  amount: string;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
  consumer_id: string;
  coupon_code: string | null;
  created_at: string;
  currency: string;
  current_period_end_at: string;
  current_period_start_at: string;
  deleted_at: string | null;
  discount_amount: string | null;
  discount_duration: string | null;
  discount_duration_months: number | null;
  discount_percent: number | null;
  interval: string;
  interval_count: number;
  last_gateway_sync: string;
  last_gateway_sync_error: string | null;
  mrr: number;
  plan_id: string;
  recurly_token_id: string;
  recurly_uuid: string;
  site_id: string;
  start_at: string;
  status: string;
  tax_percent: number | null;
  tracking_code: string | null;
  tracking_partner: string | null;
  trial_allowed: boolean;
  trial_period_days: number;
  updated_at: string;
  plan_name: string;
  consumer?: ZypeUser;
  entitlement_type: string;
  days_remaining: number;
}

export interface RecurlyBillingInfo {
  id?: string;
  object?: string;
  accountId: string;
  primaryPaymentMethod?: boolean;
  firstName: string;
  lastName: string;
  company?: string | null;
  address: {
    street1: string;
    street2?: string;
    city: string;
    region: string;
    postalCode: string;
    country: string;
    phone?: string;
  };
  vatNumber?: string;
  valid?: boolean;
  paymentMethod: {
    object: "credit_card" | "bank_account_info";
    lastFour: string;
    cardType?: string;
    firstSix?: string;
    expMonth?: number;
    expYear?: number;
    accountType?: "checking" | "savings";
    nameOnAccount?: string;
    routingNumber?: string;
    routingNumberBank?: string;
  };
  fraud?: string;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: {
    ip: string;
    country: string;
  };
}

export interface RecurlyBillingInfoCreate {
  tokenId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  address?: RecurlyAddress | null;
  nameOnAccount?: string | null;
  accountNumber?: string | null;
  routingNumber?: string | null;
  type?: string | null;
  accountType?: string | null;
}

export interface RecurlyAddress {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export interface PwsCreateSubscriptionPayload {
  subscription: {
    recurly_token_id: string;
    plan_id: string;
  };
  user?: PwsUpdateUserPayload;
}

interface PwsGiftSubscriptionDonor extends PwsUpdateUserPayload {
  name: string;
}

export interface PwsPresentGiftSubscriptionPayload {
  subscription: {
    recurly_token_id: string;
    plan_id: string;
  };
  donor: PwsGiftSubscriptionDonor;
  recipient: {
    id: string;
    name: string;
  };
}

export interface PwsPresentGiftSubscriptionResponse extends PwsBaseResponse {
  success: boolean;
  donor: ZypeUser;
}

export interface PwsUpdateUserPayload {
  name?: string;
  birthday?: string;
  updates?: boolean;
  terms?: boolean;
}

export interface PwsCreateUserPayload extends PwsUpdateUserPayload {
  password: string;
  custom?: {
    password_hint?: string;
  };
}

export interface PwsBaseResponse {
  reason?: string;
  code?: number;
}

export interface PwsLoginResponse extends PwsBaseResponse {
  loginData: ZypeLoginData;
  user?: ZypeUser;
  subscriptions?: ZypeSubscription[];
}

export interface PwsListSubscriptionPlansResponse extends PwsBaseResponse {
  plans: ZypeSubscriptionPlan[];
}

export interface PwsViewBillingInfoResponse extends PwsBaseResponse {
  billingInfo: RecurlyBillingInfo;
}

export interface PwsViewSubscriptionsResponse extends PwsBaseResponse {
  subscriptions: ZypeSubscription[];
}

export interface PwsViewSubscriptionResponse extends PwsBaseResponse {
  subscription: ZypeSubscription;
}

export interface PwsViewUserResponse extends PwsBaseResponse {
  user: ZypeUser;
  subscriptions?: ZypeSubscription[];
}

export interface PwsCreateUserResponse extends PwsBaseResponse {
  user: ZypeUser;
  loginData?: ZypeLoginData;
}

export interface PwsSubmittedResponse extends PwsBaseResponse {
  submitted: boolean;
}

export interface PwsGiftRecipientResponse extends PwsBaseResponse {
  userId: string;
}

export interface PwsPasswordHintResponse extends PwsBaseResponse {
  passwordHint: string;
}
export interface PWSMemberServiceBase {
  getUser(accessToken: string, includeSubscription?: boolean): Promise<PwsViewUserResponse>;
  getBillingInfo(accessToken: string): Promise<PwsViewBillingInfoResponse>;
}

export interface PwsOnAirStatusResponse extends PwsBaseResponse {
  isOnAir: boolean;
}

export interface PwsPremiereMediaResponseWrapper {
  data: PwsPremiereMediaResponse;
}

export interface PwsPremiereMediaListResponseWrapper {
  data: PwsPremiereMediaListResponse;
}

export interface PwsPremiereMediaResponse {
  status: string;
  message: string;
  data: PwsPremiereMediaResponseData;
}

export interface PwsPremiereMediaListResponse {
  status: string;
  message: string;
  data: {
    authenticated: boolean;
    media: PwsPremiereMediaResponseData[];
  };
}

export interface PwsPremiereMediaResponseData {
  show: string;
  type: string;
  identifier: string;
  clip: number;
  url: string;
  metadata: PwsPremiereMediaMetadata;
}

export interface PwsPremiereMediaMetadata {
  title: string;
  description: string;
  source: string;
  id: string;
  publish_date: string;
}

export interface PwsListMediaEpisodesParams {
  categories?: { [category: string]: string };
  categoryValue?: string;
  categoryName?: string;
  startDate?: number;
  groupSlug?: string;
  perPage?: number;
  endDate?: number;
  query?: string;
  page?: number;
}

export interface PwsMediaResponseBase {
  authenticated: boolean;
  playerApiKey: string;
}

export interface PwsViewMediaEpisodeResponse extends PwsMediaResponseBase {
  video: PwsMediaEpisode | null;
}

export interface PwsListMediaEpisodesResponse extends PwsMediaResponseBase {
  pagination: ZypePagination;
  videos: PwsMediaEpisode[];
  resume: string | null;
}

export interface PwsMediaOnAirStatusResponse extends PwsMediaResponseBase {
  isOnAir: boolean;
}

export interface PwsMediaIsAudioResponse extends PwsMediaResponseBase {
  isAudio: boolean;
}

export interface PwsMediaConfigResponse extends PwsMediaResponseBase {
  tabs: PwsMediaConfigTab[];
  message?: string;
}

export interface PwsMediaEpisode {
  id: string;
  title: string;
  slug: string;
  description: string;
  shortDescription: string;
  thumbnails: ZypeEpisodeThumbnail[];
  categories: ZypeEpisodeCategory[];
  publishedAt: string;
  subscriptionRequired: boolean;
  manifest: string | null;
  onAir: boolean;
  duration: number;
  isAudio?: boolean | null;
  autoplay: boolean;
}

export interface PwsMediaConfigTab {
  categories: { [name: string]: string };
  pagination?: ZypePagination;
  resume: string | null;
  videos?: PwsMediaEpisode[] | null;
  hasLive: boolean;
  title: string;
  slug: string;
  autoplay: boolean;
}

export interface ZypePagination {
  current: number;
  previous: number | null;
  next: number | null;
  per_page: number;
  pages: number;
}

export interface ZypeEpisodeThumbnail {
  aspect_ratio: number;
  height: number;
  name?: string | null;
  url: string;
  width: number;
}

export interface ZypeEpisodeCategory {
  _id: string;
  category_id: string;
  title: string;
  value: string[];
}
